import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u || ""}`);
const SLIDE_MS = 3500;

/**
 * Auto-generated virtual tour: plays the school's gallery photos as a
 * crossfading slideshow so families can quickly "walk through" a school
 * before deciding where to apply. Purely presentational — built from
 * existing gallery images, no external generation service involved.
 * Custom lightweight overlay (not the shadcn Dialog) so we get a full-bleed,
 * borderless, dark cinema-style presentation.
 */
export default function VirtualTourModal({ open, onOpenChange, images = [], schoolName }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const closeBtnRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(0);
      // Land keyboard/screen-reader focus inside the dialog as soon as it mounts.
      requestAnimationFrame(() => closeBtnRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onOpenChange(false); return; }
      if (e.key !== "Tab") return;
      // Simple focus trap: keep Tab/Shift+Tab cycling within the dialog's own controls.
      const focusables = containerRef.current?.querySelectorAll("button");
      if (!focusables || focusables.length === 0) return;
      const list = Array.from(focusables);
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open || !playing || images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, SLIDE_MS);
    return () => clearInterval(timerRef.current);
  }, [open, playing, images.length]);

  if (!open || !images.length) return null;

  const goTo = (i) => setIndex(((i % images.length) + images.length) % images.length);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
      data-testid="virtual-tour-modal"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${schoolName} sanal tur`}
        className="relative w-full max-w-3xl aspect-[16/10] bg-black rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <img
            key={img + i}
            src={resolveUrl(img)}
            alt={`${schoolName} sanal tur ${i + 1}`}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
            style={{ opacity: i === index ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        <button
          ref={closeBtnRef}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition"
          onClick={() => onOpenChange(false)}
          aria-label="Sanal turu kapat"
          data-testid="virtual-tour-close"
        >
          <X className="h-4 w-4" />
        </button>

        <button
          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition"
          onClick={() => goTo(index - 1)}
          aria-label="Önceki fotoğraf"
          data-testid="virtual-tour-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition"
          onClick={() => goTo(index + 1)}
          aria-label="Sonraki fotoğraf"
          data-testid="virtual-tour-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div className="absolute bottom-0 inset-x-0 p-5">
          <div className="text-white font-display font-bold text-lg mb-1">{schoolName} · Sanal Tur</div>
          <div className="flex items-center gap-3">
            <button
              className="p-1.5 rounded-full bg-white/15 text-white hover:bg-white/25 transition"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? "Duraklat" : "Oynat"}
              data-testid="virtual-tour-play-pause"
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
            <div className="flex-1 flex gap-1.5" role="tablist" aria-label="Fotoğraflar">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="h-1 flex-1 rounded-full overflow-hidden bg-white/25"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`${i + 1}. fotoğrafa geç`}
                  data-testid={`virtual-tour-dot-${i}`}
                >
                  <span
                    className="block h-full bg-white transition-all"
                    style={{ width: i <= index ? "100%" : "0%" }}
                  />
                </button>
              ))}
            </div>
            <span className="text-white/70 text-xs font-mono shrink-0">{index + 1}/{images.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

